// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Realmfold — every screen that is not the board: the menu, the two sheets, and the results.
//
// THE RESULTS SCREEN SHOWS EVERY PLAYER, EVERY TIME, and shows what each one actually DID rather
// than a name and a number: their demesne, every region as `land · cells × coronets = points`, the
// bonuses they earned and the ones they missed, and what they had to throw away. There is no hidden
// information in this game, so there is no excuse for a summary that only reflects you back at
// yourself — that moment is the one time players compare themselves, and a bare score wastes it.

import { escapeHtml } from './dom';
import { TERRAIN_KEYS, TERRAIN_NAME, SEAT_COLOUR } from './palette';
import { miniHtml } from './render';
import { MODE_IDS, modeOf, roundsIn, type Mode } from './modes';
import { TIER_IDS, tierOf } from './bot';
import { DECK_STATS } from './rules';
import type { ResultRow } from './session';
import type { Demesne } from './rules';

export const ATTRIB =
  'Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>';

export function menuHtml(o: { modeId: string; tierId: string; best: number; muted: boolean }): string {
  const mode = modeOf(o.modeId);
  return `
  <section class="screen menu">
    <h1 class="title">Realmfold</h1>
    <p class="tag">Take the tile you want and you pick last next round.</p>

    <div class="picker" role="group" aria-label="mode">
      ${MODE_IDS.map(
        (id) =>
          `<button class="chip${id === o.modeId ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === o.modeId}">${escapeHtml(
            modeOf(id).name,
          )}</button>`,
      ).join('')}
    </div>
    <p class="blurb">${escapeHtml(mode.blurb)}</p>

    <div class="picker small" role="group" aria-label="opponent">
      ${TIER_IDS.map(
        (id) =>
          `<button class="chip${id === o.tierId ? ' on' : ''}" data-tier="${id}" aria-pressed="${id === o.tierId}">${escapeHtml(
            tierOf(id).name,
          )}</button>`,
      ).join('')}
    </div>
    <p class="blurb small">${escapeHtml(tierOf(o.tierId).blurb)}</p>

    <div class="menu-actions">
      <button class="btn primary" id="playSolo">Play</button>
      <button class="btn" id="playFriends">Play with friends</button>
      <button class="btn ghost" id="help">How to play</button>
      <button class="btn ghost" id="about">About</button>
      <button class="btn ghost" id="mute" aria-pressed="${o.muted}">${o.muted ? 'Sound off' : 'Sound on'}</button>
    </div>
    ${o.best > 0 ? `<p class="best">Your best ${escapeHtml(mode.name)} demesne: <strong>${o.best}</strong></p>` : ''}
  </section>`;
}

export function helpHtml(mode: Mode): string {
  return `
  <h2>How to play</h2>
  <p>Claim a doublet from the row and lay it on your demesne, matching land to land — or against the
     <strong>Motte</strong> in the middle, which matches anything. One half is enough.</p>
  <p>A region scores <strong>its size × its coronets</strong>. Eight cells of Wold with no coronets
     is worth nothing; three cells of Coomb with three coronets is worth nine.</p>
  <p>The number you claim decides when you pick next round — <strong>lowest goes first</strong>. Grab
     the fat one and you pick last. That trade is the game.</p>
  <p class="mode-rule"><strong>${escapeHtml(mode.name)}:</strong> ${escapeHtml(mode.rule)}</p>
  <p class="fine">Nothing fits? The doublet is thrown away and two cells stay empty — which costs you
     the tidy-demesne bonus, so keep somewhere for everything.</p>
  <button class="btn sheet-close">Got it</button>`;
}

export function aboutHtml(): string {
  return `
  <h2>About</h2>
  <p>Realmfold is a tile-drafting game about the price of going first. ${DECK_STATS.doublets} doublets,
     ${DECK_STATS.cells} cells of six lands and ${DECK_STATS.coronets} coronets between them — and the
     only way to take the good one is to give up the initiative.</p>
  <p>Every match draws its doublets one from each band of the deck, so no two players ever face a
     wildly richer or poorer deal than each other.</p>
  <h3>Playing with friends</h3>
  <p>Rooms are peer-to-peer. There is no game server and no account: your browsers talk directly to
     each other, and a public signalling relay is used only to introduce them. Nothing about your
     match is stored anywhere. Connecting exchanges network addresses with the people you invited,
     which is how any direct connection works.</p>
  <p class="fine">No cookies, no fingerprinting, no third-party fonts. Anonymous, cookie-less
     page-view counts via Cloudflare Web Analytics — that is the only measurement here.</p>
  <p class="fine">${ATTRIB}</p>
  <button class="btn sheet-close">Close</button>`;
}

export function waitingHtml(names: string[], turn: number, absent: boolean): string {
  const who = escapeHtml(names[turn] ?? 'the next player');
  return absent
    ? `<span class="spinner"></span> ${who} has gone quiet — the table will play their turn shortly…`
    : `<span class="spinner"></span> Waiting for ${who}…`;
}

/** One player's full breakdown. Not a leaderboard row — what they actually built. */
function rowHtml(r: ResultRow, board: Demesne, side: number, mode: Mode, rounds: number): string {
  const regions = r.tally.regions
    .filter((g) => g.cells > 0)
    .slice(0, 8)
    .map(
      (g) =>
        `<li class="rg${g.points === 0 ? ' nil' : ''}"><i class="sw t-${TERRAIN_KEYS[g.terrain]}"></i>${escapeHtml(
          TERRAIN_NAME[TERRAIN_KEYS[g.terrain]],
        )} <span class="rgm">${g.cells} × ${g.coronets}</span> <b>${g.points}</b></li>`,
    )
    .join('');
  const bonus: string[] = [];
  if (mode.wholefold) {
    bonus.push(
      r.tally.wholefold === 10
        ? '<li class="bn got">Wholefold — not a cell wasted <b>+10</b></li>'
        : `<li class="bn${r.tally.wholefold > 0 ? ' got' : ' missed'}">Wholefold — ${r.tally.empty} cell${
            r.tally.empty === 1 ? '' : 's'
          } left empty <b>+${r.tally.wholefold}</b></li>`,
    );
  }
  bonus.push(
    r.tally.sixfold > 0
      ? '<li class="bn got">Sixfold — all six lands <b>+5</b></li>'
      : `<li class="bn missed">Sixfold — ${6 - r.tally.terrains} land${6 - r.tally.terrains === 1 ? '' : 's'} missing <b>+0</b></li>`,
  );
  if (mode.holdfast) {
    const big = r.tally.regions.filter((g) => g.cells >= 6).length;
    bonus.push(
      `<li class="bn${big > 0 ? ' got' : ' missed'}">Holdfast — ${big} region${big === 1 ? '' : 's'} of six or more <b>+${r.tally.holdfast}</b></li>`,
    );
  }
  if (r.spoiled > 0) {
    bonus.push(`<li class="bn missed">Thrown away — ${r.spoiled} doublet${r.spoiled === 1 ? '' : 's'} with nowhere to go</li>`);
  }
  return `
  <article class="rrow${r.won ? ' won' : ''}${r.isSelf ? ' me' : ''}" style="--seat:${SEAT_COLOUR[r.seat % SEAT_COLOUR.length]}">
    <header>
      <span class="rn">${escapeHtml(r.name)}${r.isSelf ? ' <em>(you)</em>' : ''}${r.isBot ? ' <em>(bot)</em>' : ''}${
        r.absent ? ' <em>(left)</em>' : ''
      }</span>
      <span class="rt">${r.tally.total}</span>
    </header>
    <div class="rbody">
      ${miniHtml(board, side, 'big')}
      <div class="rlists">
        <ul class="regions">${regions || '<li class="rg nil">nothing placed</li>'}</ul>
        <ul class="bonuses">${bonus.join('')}</ul>
      </div>
    </div>
    <p class="rfoot">${r.tally.coronets} coronets · largest region ${r.tally.largest} · ${rounds} rounds</p>
  </article>`;
}

export function resultsHtml(o: {
  rows: ResultRow[];
  boards: Demesne[];
  side: number;
  mode: Mode;
  players: number;
  tally: number[];
  tallyNames: string[];
  round: number;
  fault: string | null;
}): string {
  const sorted = o.rows.slice().sort((a, b) => b.tally.total - a.tally.total);
  const win = sorted.filter((r) => r.won);
  const head = o.fault
    ? o.fault === 'build'
      ? 'Someone is on a different build of Realmfold — reload to play again.'
      : 'The tables stopped agreeing, so the match was stopped.'
    : win.length > 1
      ? `${win.map((r) => escapeHtml(r.name)).join(' and ')} tie on ${win[0].tally.total}`
      : `${escapeHtml(win[0]?.name ?? '')} wins with ${win[0]?.tally.total ?? 0}`;

  const match =
    o.tally.some((t) => t > 0) && o.tallyNames.length > 0
      ? `<p class="matchtally">${o.tallyNames.map((n, i) => `${escapeHtml(n)} ${o.tally[i] ?? 0}`).join(' · ')}</p>`
      : '';

  return `
  <h2 class="rhead">${head}</h2>
  ${match}
  <p class="rsub">${escapeHtml(o.mode.name)} · round ${o.round}</p>
  <div class="results">${sorted.map((r) => rowHtml(r, o.boards[r.seat], o.side, o.mode, roundsIn(o.mode, o.players))).join('')}</div>`;
}
